import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Button,
  ClinicalFlagBadge,
  EmptyState,
  MedicationDayLadder,
  PageLoading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  inferDoseHoursFromFrequency,
} from "@dosa/ui";
import type { MtmAssessmentType } from "@dosa/types";
import { useAuth } from "../../hooks/useAuth";
import {
  getPatient,
  getPatientClinicalFlags,
  listPatientAllergies,
  listPatientConditions,
  listPatientMedications,
  startMtmAssessment,
} from "../../lib/api-client";

const ASSESSMENT_TYPES: MtmAssessmentType[] = [
  "INITIAL_MTM",
  "FOLLOW_UP",
  "POST_DISCHARGE_REVIEW",
  "POLYPHARMACY_REVIEW",
  "CHRONIC_MEDICATION_MANAGEMENT",
];

export function PharmacistPatientProfilePage() {
  const { t } = useTranslation();
  const { patientId } = useParams<{ patientId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessmentType, setAssessmentType] = useState<MtmAssessmentType>("INITIAL_MTM");

  const patientQuery = useQuery({ queryKey: ["patient", patientId], queryFn: () => getPatient(patientId!), enabled: !!patientId });
  const medsQuery = useQuery({ queryKey: ["patient-medications", patientId], queryFn: () => listPatientMedications(patientId!), enabled: !!patientId });
  const conditionsQuery = useQuery({ queryKey: ["patient-conditions", patientId], queryFn: () => listPatientConditions(patientId!), enabled: !!patientId });
  const allergiesQuery = useQuery({ queryKey: ["patient-allergies", patientId], queryFn: () => listPatientAllergies(patientId!), enabled: !!patientId });
  const flagsQuery = useQuery({ queryKey: ["clinical-flags", patientId], queryFn: () => getPatientClinicalFlags(patientId!), enabled: !!patientId });

  const startMutation = useMutation({
    mutationFn: () => startMtmAssessment(patientId!, user!.pharmacistId!, assessmentType),
    onSuccess: (assessment) => navigate(`/pharmacist/assessment/${assessment.id}`),
  });

  if (patientQuery.isLoading) return <PageLoading />;
  if (!patientQuery.data) return <EmptyState title={t("common.noResults")} />;

  const patient = patientQuery.data;
  const activeMeds = (medsQuery.data ?? []).filter((m) => m.status === "ACTIVE");
  const conditionsLine = (conditionsQuery.data ?? []).map((c) => c.freeTextName).filter(Boolean).join(" · ");
  const allergyLine = (allergiesQuery.data ?? []).map((a) => a.freeTextAllergen).filter(Boolean).join(", ");

  return (
    <div className="space-y-9">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="flex flex-wrap items-end gap-4">
            <h1 className="text-[clamp(1.75rem,3.4vw,2.375rem)] font-semibold leading-none">
              {patient.firstName} {patient.lastName}
            </h1>
            <div className="flex flex-wrap items-center gap-3 pb-1.5">
              <span className="text-sm tabular-nums text-charcoal-500">{t("pharmacist.patients.mrn")} {patient.patientCode}</span>
              {allergyLine && (
                <span className="border border-clinical-high px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-clinical-high">
                  {t("patient.allergies.title")} · {allergyLine}
                </span>
              )}
            </div>
          </div>
          {conditionsLine && <p className="mt-2 text-[15px] text-charcoal-500">{conditionsLine}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={assessmentType}
            onValueChange={(v) => setAssessmentType(v as MtmAssessmentType)}
            items={Object.fromEntries(ASSESSMENT_TYPES.map((type) => [type, type.replaceAll("_", " ")]))}
          >
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSESSMENT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {type.replaceAll("_", " ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => startMutation.mutate()} isLoading={startMutation.isPending}>
            {t("pharmacist.patientProfile.startAssessment")}
          </Button>
        </div>
      </div>

      {flagsQuery.data && flagsQuery.data.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.patientProfile.clinicalFlags")}
          </p>
          <div className="flex flex-wrap gap-2">
            {flagsQuery.data.map((f, idx) => (
              <ClinicalFlagBadge key={idx} severity={f.severity} label={t(`clinicalFlags.${f.type}`)} />
            ))}
          </div>
        </div>
      )}

      {activeMeds.length > 0 && (
        <div>
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.patientProfile.dayLadder")}
          </p>
          <div className="overflow-x-auto pb-1">
            <MedicationDayLadder
              medications={activeMeds.map((m) => ({
                id: m.id,
                label: [m.freeTextName, m.dose].filter(Boolean).join(" "),
                doseHours: inferDoseHoursFromFrequency(m.frequency),
              }))}
            />
          </div>
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.patientProfile.medications")}
          </p>
          {activeMeds.length ? (
            <ul>
              {activeMeds.map((m) => (
                <li key={m.id} className="flex items-baseline justify-between gap-4 border-t border-charcoal-900/[0.14] py-3 text-sm">
                  <span className="font-medium text-charcoal-900">{m.freeTextName}</span>
                  <span className="text-charcoal-500">{[m.dose, m.frequency?.replaceAll("_", " ")].filter(Boolean).join(" · ")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-charcoal-500">{t("patient.medications.empty")}</p>
          )}
        </div>
        <div>
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-700">
            {t("pharmacist.patientProfile.conditions")}
          </p>
          {conditionsQuery.data?.length ? (
            <ul>
              {conditionsQuery.data.map((c) => (
                <li key={c.id} className="flex items-center justify-between border-t border-charcoal-900/[0.14] py-3 text-sm">
                  <span className="text-charcoal-900">{c.freeTextName}</span>
                  <span className="text-[11px] uppercase tracking-[0.05em] text-charcoal-500">{c.status.replaceAll("_", " ")}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-charcoal-500">{t("patient.conditions.empty")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
